import { defineStore } from 'pinia'
import Pusher, { Channel } from 'pusher-js'

export interface RecruitmentNotificationData {
  driver_id?: string
  driver_name?: string
  first_name?: string
  last_name?: string
  phone_number?: string
  email?: string
  driver_state?: string
  old_state?: string
  new_state?: string
  reason?: string
  updated_by?: string
  timestamp?: string
  application_date?: string
  created_at?: string
}

export interface RecruitmentNotification {
  id: string
  type: 'new_driver' | 'state_change'
  message: string
  data: RecruitmentNotificationData
  seen: boolean
  timestamp: string
}

const CHANNEL_NAME = 'driver-recruitment-channel'
const MAX_NOTIFICATIONS = 50

export const useRecruitmentNotificationsStore = defineStore('recruitmentNotifications', {
  state: () => ({
    pusher: null as Pusher | null,
    channel: null as Channel | null,
    notifications: [] as RecruitmentNotification[],
  }),

  getters: {
    unreadCount: (state) => state.notifications.filter((n) => !n.seen).length,
    recentNotifications: (state) => state.notifications.slice(0, 20),
  },

  actions: {
    /**
     * Initialize Pusher and subscribe to driver-recruitment-channel (public channel, no auth)
     */
    initializePusher(apiKey: string, cluster: string) {
      if (this.pusher) return

      this.pusher = new Pusher(apiKey, {
        cluster,
        encrypted: true,
      })

      this.pusher.connection.bind('state_change', (states: { current: string }) => {
        console.log('[Recruitment Pusher] Connection state:', states.current)
      })

      this.channel = this.pusher.subscribe(CHANNEL_NAME)

      // New driver registered
      this.channel.bind('new-driver-registered', (data: RecruitmentNotificationData) => {
        const driverName =
          data.driver_name || [data.first_name, data.last_name].filter(Boolean).join(' ') || 'Unknown driver'
        this.addNotification('new_driver', `New driver: ${driverName}`, data)
      })

      // Applicant state changed
      this.channel.bind('driver-applicant-state-change', (data: RecruitmentNotificationData) => {
        const driverName =
          data.driver_name || [data.first_name, data.last_name].filter(Boolean).join(' ') || 'Unknown driver'
        const message = `${driverName}: ${data.old_state || '?'} → ${data.new_state || '?'}`
        this.addNotification('state_change', message, data)
      })
    },

    addNotification(type: 'new_driver' | 'state_change', message: string, data: RecruitmentNotificationData) {
      this.notifications.unshift({
        id: `rec-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type,
        message,
        data,
        seen: false,
        timestamp: new Date().toISOString(),
      })

      if (this.notifications.length > MAX_NOTIFICATIONS) {
        this.notifications = this.notifications.slice(0, MAX_NOTIFICATIONS)
      }
    },

    markAsSeen(id: string) {
      const n = this.notifications.find((x) => x.id === id)
      if (n) n.seen = true
    },

    markAllAsSeen() {
      this.notifications.forEach((n) => (n.seen = true))
    },

    disconnect() {
      if (this.channel) {
        this.channel.unbind_all()
        this.channel.unsubscribe()
        this.channel = null
      }
      if (this.pusher) {
        this.pusher.disconnect()
        this.pusher = null
      }
    },
  },
})
